/**
 * ENT-4200: a read-only, synchronous probe for a persisted Supabase session.
 *
 * This is a PRESENTATION-support utility, deliberately kept OUT of the
 * security-critical `src/lib/auth.ts` module: it makes no auth/session/security
 * decision. Its sole job is to let the sign-in surface decide, at first paint,
 * whether to show a "resolving session" gate instead of flashing the sign-in
 * form for a visitor who is already signed in — before the async
 * `supabase.auth.getSession()` round-trip resolves. `getSession()` remains the
 * source of truth for routing; a truthy result here means only "a session token
 * is stored", not that it is unexpired.
 *
 * It matches the canonical `sb-<ref>-auth-token` localStorage key that
 * `clearLocalAuthState` (auth.ts) writes and removes — the Supabase default
 * storageKey convention — so it stays correct across loopback (custom
 * storageKey) and deployed (default storageKey) hosts. It does NOT catch a fresh
 * SSO callback whose token is still in the URL hash and not yet persisted; that
 * case is gated on the `?sso_return=1` marker instead.
 */
export function hasStoredSupabaseSession(): boolean {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key) {
        continue;
      }
      if (key.startsWith('sb-') && key.endsWith('-auth-token') && localStorage.getItem(key)) {
        return true;
      }
    }
  } catch {
    // localStorage may be unavailable in some contexts — treat as no session.
  }
  return false;
}
